import { Breakpoint } from '@mui/material';

// Typescript module augmentation
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }
}

const breakpoints: Record<Breakpoint, number> = {
  xs: 0,
  sm: 640,
  //md: 768,
  md: 840,
  lg: 1080,
  xl: 1440,
  xxl: 2160,
};

const colors = {
  // TODO: replace with the active color palette and remove the active colors
  primary: '#1890ff',
  primaryDark: '#0066FF',
  primaryMedium: '#9FE0FF',
  primaryLight: '#E6F7FF',
  primaryTrans: 'rgba(24, 144, 255, 0.1)',
  white: '#fff',
  black: '#565656',
  success: '#06d6a0',
  successLight: '#c6f5e2',
  info: '#db00ff',
  active: '#06d6a0',
  activeLight: '#c6f5e2',
  notice: '#faad14',
  darkBlue: '#08979C',
  lightGreen: '#EDFBF8',
  transOrange: 'rgba(255, 149, 0, 0.1)',
  green: '#44D5B4',
  darkGreen: '#52c41a',
  lightGreen2: '#f6ffed',
  anotherGreen: '#17AB13',
  purple: '#7B61FF',
  transPurple: 'rgba(123, 97, 255, 0.1)',
  yellow: '#c19400',
  transYellow: '#fff1c1',
  lightYellow2: '#feffe6',
  hoverRed: '#fbcacd',
  red: '#F13642',
  redOpaque: '#fde2e4',
  redDark: '#BD222C',
  transRed: 'rgba(241, 54, 66, 0.1)',
  teal: '#009688',
  tealDark: '#00796B',
  tealLight: '#f3f9f8',
  cyan: '#00bcd4',
  cyanLight: '#f4fbfc',
  lookerLoading: '#1a73e8',
  mint: '#96F6DD',
  orange: '#FF9500',
  paleOrange: '#ffe6bc',
  amber: '#F98702',
  amberDark: '#e47b00',
  amberMedium: '#FFEDD2',
  amberLight: '#FCF4E8',
  statuses: {
    ORDERED: {
      background: '#F0F0F0',
      foreground: '#595959', // Dark Grey
      muted: '#D9D9D9',
    },
    PARTIALLY_DISPATCHED: {
      background: '#EFDBFF',
      foreground: '#531DAB', // Mauve
      muted: '#D4C9ED',
    },
    DISPATCHED: {
      background: '#F8E4FD',
      foreground: '#A702D0', // purple
      muted: '#A702D04A',
    },
    PARTIALLY_DELIVERED: {
      background: '#FFF1B8',
      foreground: '#AD8B00', // dark mustard
      muted: '#AD8B004A',
    },
    DELIVERED: {
      background: '#FFFBE6',
      foreground: '#AD8B00', // mustard
      muted: '#AD8B004A',
    },
    PARTIALLY_RECEIVED: {
      background: '#FFBB96',
      foreground: '#D4380D', // red-orange
      muted: '#D4380D4A',
    },
    RECEIVED: {
      background: '#FFF7E6',
      foreground: '#FA8C16', // orange
      muted: '#FA8C164A',
    },
    PARTIALLY_CHECKED_IN: {
      background: '#f3f9f8',
      foreground: '#009688', // teal
      muted: '#0096884A',
    },
    CHECKED_IN: {
      background: '#E5FFF8',
      foreground: '#06D4A0', // green
      muted: '#06D6A04A',
    },
    PARTIALLY_CHECKED_OUT: {
      background: '#f4fbfc',
      foreground: '#00BCD4', // cyan
      muted: '#00BCD44A',
    },
    CHECKED_OUT: {
      background: '#EBF3FC',
      foreground: '#1890FF',
      muted: '#1890FF4A',
    },
    PARTIALLY_RETURNED: {
      background: '#f1f2f6',
      foreground: '#2E40A3',
      muted: '#2E40A34A',
    },
    RETURNED: {
      background: '#dce3f8',
      foreground: '#1D39C4',
      muted: '#1D39C44A',
    },
    CANCELED: {
      background: '#FFFFFF',
      foreground: '#DE2727', // red
      muted: '#DE27274A',
    },
    DELETED: {
      background: '#FFE7E7',
      foreground: '#DE2727', // red
      muted: '#DE27274A',
    },
    NOT_DELIVERED: {
      background: '#FFFFFF',
      foreground: '#000000', // black
      muted: '#0000004A',
    },
  },
};

export class ThemeCfg {
  colors = colors;
  // legacy name
  colours = colors; // TODO: SS remove this
  greys = {
    GREY0: '#e5e5e5',
    // NOTE I changed GREY1 and GREY4 to match the MUI form field greys,
    //  if it does work in other usages we can revert or make a new grey
    GREY1: '#C4C4BB', //'#bfbfbf',
    GREY2: '#d9d9d9',
    GREY3: '#989898',
    GREY4: '#666666', // '#6f6f6f',
    GREY5: '#eee',
    GREY6: '#f5f6f8',
    GREYFA: '#fafafa',
    GREY6C: '#6C757D',
    GREYE8: '#E8E8E8',
    GREYF4: '#F4F4F4',
    GREYF5: '#F5F5F5',
    GREYA2: '#A2A2A2',
    GREY3C: '#3C3C3C',
    GREYBF: '#BFBFBF',
    GREYF2: '#F2F3F5',
  };
  font = "'Poppins', sans-serif";
  breakpoints = {
    values: breakpoints,
    keys: Object.keys(breakpoints) as Breakpoint[],
  };
  mediaQueries = {
    desktop: `@media screen and (min-width: ${breakpoints.lg}px)`,
    xlarge: `@media screen and (min-width: ${breakpoints.xl}px)`,
  };
  layers = {
    bottom: 0,
    top: 10000,
  };
  layout = {
    spacing: {
      tiny: '4px',
      small: '8px',
      medium: '16px',
      large: '32px',
      extraLarge: '64px',
    },
  };
  sizes = {
    rowHeight: 80,
    rowHeightCompact: 64,
    minInputWidth: 300,
  };
}

export const lightenDarkenColor = (color: string, amount: number): string => {
  if (!color.startsWith('#')) return color;

  // Remove the hash symbol
  color = color.slice(1);

  // Expand shorthand hex color to full form
  if (color.length == 3) {
    color = color
      .split('')
      .map(char => char + char)
      .join('');
  }

  // Parse the color into its RGB components
  let r = parseInt(color.substring(0, 2), 16);
  let g = parseInt(color.substring(2, 4), 16);
  let b = parseInt(color.substring(4, 6), 16);

  // Define a function to clamp the values between 0 and 255
  const clip = (n: number) => Math.min(Math.max(n, 0), 255);

  // Adjust each component and clamp the values
  r = clip(r + amount);
  g = clip(g + amount);
  b = clip(b + amount);

  // Convert the adjusted RGB values back to a hex string
  const newColor = ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);

  return `#${newColor}`;
};

const theme = new ThemeCfg();
export default theme;
